import { Component } from 'react'
import * as isNumber from 'lodash/isNumber'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/CreditChart/CreditChart.css'
import { CommonChartValues } from 'interfaces/chart'
import Tooltip from 'components/features/pfm/Tooltip/Tooltip'

export type CreditChartProps =
  & {
    showTooltip?: boolean
    withComparison: boolean
  }
  & {
    [T in keyof CommonChartValues]: CommonChartValues[T] | null
  }

class CreditChart extends Component<CreditChartProps> {
  private creditBarNode: HTMLDivElement | null = null
  private memberValueMarkerNode: HTMLElement | null = null
  private peersScoreMarkerNode: HTMLElement | null = null
  private otherValueMarkerNode: HTMLElement | null = null

  static defaultProps = {
    withComparison: true,
  }

  render () {
    const { memberValue, peerValue, otherValue, showTooltip, withComparison } = this.props

    const memberTooltip = showTooltip ? (
      <div className={ styles.tooltipContainer }>
        <Tooltip theme='member'>{ memberValue }</Tooltip>
      </div>
    ) : null

    return (
      <div ref={ (el) => this.creditBarNode = el } className={ styles.creditChart }>
        <div className={ cn([styles.creditBarPortion, styles.low]) }>
          <span className={ styles.creditBarLabels }>low</span>
        </div>
        <div className={ cn([styles.creditBarPortion, styles.fair]) }>
          <span className={ styles.creditBarLabels }>fair</span>
        </div>
        <div className={ cn([styles.creditBarPortion, styles.good]) }>
          <span className={ styles.creditBarLabels }>good</span>
        </div>
        <div className={ cn([styles.creditBarPortion, styles.great]) }>
          <span className={ styles.creditBarLabels }>great</span>
        </div>
        <div className={ cn([styles.creditBarPortion, styles.excellent]) }>
          <span className={ styles.creditBarLabels }>excellent</span>
        </div>
        { isNumber(memberValue) && (
          <i ref={ (el) => this.memberValueMarkerNode = el } className={ cn(styles.creditMarker, styles.member) }>
            { memberTooltip }
          </i>
        )}
        { withComparison && isNumber(otherValue) && (
          <i
            ref={ (el) => this.otherValueMarkerNode = el }
            className={ cn(styles.creditMarker, styles.others) }
          />
        )}
        { withComparison && isNumber(peerValue) && (
          <i
            ref={ (el) => this.peersScoreMarkerNode = el }
            className={ cn(styles.creditMarker, styles.peers) }
          />
        )}
      </div>
    )
  }

  componentDidMount () {
    this.moveAllMarkers()
  }

  componentDidUpdate () {
    this.moveAllMarkers()
  }

  private moveMarkerOptionally (node: HTMLElement | null, score: number | null) {
    if (node && isNumber(score)) {
      this.moveMarker(node, score)
    }
  }

  private resetNodeTopOptionally (node: HTMLElement | null) {
    if (node) {
      node.style.top = styles.defaultMarkerTop
    }
  }

  private moveMarker (el: HTMLElement, score: number) {
    if (!this.creditBarNode) {
      throw new Error(`CreditChart tries to run method moveMarker when bar ref is not attached`)
    }

    const totalScores = 430
    const usableScore = score >= totalScores ? score : totalScores
    const creditBarWidth = this.creditBarNode.clientWidth
    const ratio = creditBarWidth / totalScores
    const offsetAmount = (usableScore - totalScores) * ratio
    const offsetAmountPercentage = offsetAmount * 100 / creditBarWidth
    el.style.left = `${ offsetAmountPercentage }%`
  }

  private moveAllMarkers () {
    this.moveMarkerOptionally(this.memberValueMarkerNode, this.props.memberValue)
    this.moveMarkerOptionally(this.peersScoreMarkerNode, this.props.peerValue)
    this.moveMarkerOptionally(this.otherValueMarkerNode, this.props.otherValue)
    this.handleEqualValues()
  }

  private handleEqualValues () {
    const { memberValue, peerValue, otherValue } = this.props
    if (
      // all three values exist
      this.memberValueMarkerNode && this.peersScoreMarkerNode && this.otherValueMarkerNode
      // and they are equal
      && peerValue === otherValue && peerValue === memberValue
    ) {
      this.peersScoreMarkerNode.style.top = '-11px'
      this.otherValueMarkerNode.style.top = '-16px'
      return
    }

    if (
      // peer and value exist
      this.memberValueMarkerNode && this.peersScoreMarkerNode
      // and both are equal
      && peerValue === memberValue
    ) {
      // peerValue is a second priority
      this.peersScoreMarkerNode.style.top = '-11px'
      this.resetNodeTopOptionally(this.otherValueMarkerNode)
      return
    }

    if (
      // other value exists
      this.otherValueMarkerNode
      // and equals to another value
      && (otherValue === memberValue || otherValue === peerValue)
    ) {
      // otherValue has a third priority
      this.resetNodeTopOptionally(this.peersScoreMarkerNode)
      this.otherValueMarkerNode.style.top = '-11px'
      return
    }

    // or just default positions
    this.resetNodeTopOptionally(this.peersScoreMarkerNode)
    this.resetNodeTopOptionally(this.otherValueMarkerNode)
  }
}

export default CreditChart
